/**
 * Button — meta-component over the shadcn Button primitive.
 *
 * Behaviour is declared **exclusively** via props. JSX children are NOT
 * accepted (the type rejects them) so call sites can never bypass the prop
 * API by stuffing icons or spans into the button as raw markup. This keeps
 * styling, sizing, and accessibility centralised in this component.
 *
 *   <Button label="Save" preIcon={Save} />
 *   <Button label="Next" postIcon={ArrowRight} />
 *   <Button icon={Trash2} variant="destructive" />     // icon-only
 *   <Button label="Reload" preIcon={RefreshCw} loading={isPending} />
 *   <Button label="Sign in" size="xl" />               // login-page sized
 *   <Button label="Sensitive" preIcon={Zap} orientation="vertical" />
 *   <Button icon={Bell} badge />                       // dot indicator
 *   <Button icon={Bell} badge={3} />                   // count
 *   <Button icon={Bell} badge={{ count: 12, max: 9 }} /> // shows "9+"
 *   <Button label="Create key" disabled={atLimit} disabledReason="Revoke a key first." />
 *
 * Variant overrides applied at this layer (not in shadcn primitives):
 *  - default:  hover lightens to bg-primary-alt (the brighter brand blue),
 *              except on a pressed toggle (aria-pressed="true"), which keeps
 *              its chosen colour under the pointer so it never looks unchosen
 *  - outline:  truly transparent fill in both light and dark modes; hover
 *              switches text to text-primary-alt for branded feedback
 *  - ghost:    hover text → text-primary-alt for the same branded feedback
 *
 * If you genuinely need a button with bespoke content (e.g. a Popover trigger
 * containing a user avatar + name block), drop down to a raw <button> element
 * — the meta-Button is intentionally not the right tool for that case.
 */

import type { ComponentProps, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Button as ShadButton } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { FOCUS_RING } from '../_styles';
import { Spinner } from '../Spinner/Spinner';
import { Tooltip } from '../Tooltip/Tooltip';

type BaseVariant = ComponentProps<typeof ShadButton>['variant'];
type LogicalSize = 'xs' | 'sm' | 'default' | 'lg' | 'xl';
type Orientation = 'horizontal' | 'vertical';

/** MUI-style badge config. Three accepted forms:
 *  - `true`        → dot indicator (no number)
 *  - `number`      → count, capped at max=9 by default ("9+" overflow)
 *  - object form   → full control: { count, dot, max, showZero, tone }
 */
export type ButtonBadge =
  | true
  | number
  | {
      count?: number;
      /** Force dot mode regardless of count. */
      dot?: boolean;
      /** Largest number rendered as-is. Counts above show "{max}+". Default 9. */
      max?: number;
      /** Render the badge when count is 0. Default false. */
      showZero?: boolean;
      /** Colour tone. Default 'destructive' (notification red). */
      tone?: 'destructive' | 'primary' | 'success' | 'warning';
    };

const BADGE_TONE: Record<
  NonNullable<Exclude<ButtonBadge, true | number>['tone']>,
  string
> = {
  destructive: 'bg-destructive text-white',
  primary: 'bg-primary text-primary-foreground',
  success: 'bg-success-700 text-white dark:bg-success-600',
  warning: 'bg-warning-500 text-white',
};

export interface ButtonProps extends Omit<ComponentProps<'button'>, 'children'> {
  label?: string;
  icon?: LucideIcon;
  preIcon?: LucideIcon;
  postIcon?: LucideIcon;
  loading?: boolean;
  variant?: BaseVariant;
  size?: LogicalSize;
  /** Stacks the icon above the label when 'vertical'. Used for preset card
   * style buttons. Defaults to 'horizontal'. */
  orientation?: Orientation;
  /** MUI-style badge in the top-right corner. Pass `true` for a dot, a
   * number for a count, or an object for full control. */
  badge?: ButtonBadge;
  /**
   * Why the button is disabled, shown in a tooltip while `disabled` is true.
   * A disabled button fires no pointer events, so the tooltip sits on a
   * focusable wrapper. Never use a native `title` for this: it cannot show.
   * PermissionGate and RoleGate drop it: their own message wins.
   */
  disabledReason?: ReactNode;
}

/** Map our logical size to the underlying shadcn size token. `xl` is added
 * via custom className overrides since shadcn doesn't have an xl variant. */
const SHAD_SIZE: Record<LogicalSize, ComponentProps<typeof ShadButton>['size']> = {
  xs: 'xs',
  sm: 'sm',
  default: 'default',
  lg: 'lg',
  xl: 'lg', // closest baseline; we add `h-12 px-6 text-base` via XL_OVERRIDE
};

const ICON_SIZE: Record<LogicalSize, ComponentProps<typeof ShadButton>['size']> = {
  xs: 'icon-xs',
  sm: 'icon-sm',
  default: 'icon',
  lg: 'icon-lg',
  xl: 'icon-lg',
};

/** Extra classes when size === 'xl'. Tall, generous padding, larger text and
 * icons — sized for a hero submit button (e.g. the login page). */
const XL_OVERRIDE = 'h-12 px-6 text-base [&_svg:not([class*=size-])]:size-5';
const XL_ICON_OVERRIDE = 'size-12 [&_svg:not([class*=size-])]:size-5';

/** Vertical orientation: stack icon over label, expand height. */
const VERTICAL_OVERRIDE = 'flex flex-col h-auto gap-1.5 py-3 [&_svg:not([class*=size-])]:size-5';

/** Per-variant overrides applied on top of the shadcn variant classes. */
const VARIANT_OVERRIDES: Partial<Record<NonNullable<BaseVariant>, string>> = {
  default: 'hover:bg-primary-alt aria-pressed:hover:bg-primary',
  outline:
    'bg-transparent dark:bg-transparent hover:bg-accent hover:text-primary-alt dark:hover:bg-accent',
  ghost: 'hover:text-primary-alt dark:hover:text-primary-alt',
};

/**
 * Resolve a `ButtonBadge` value into the rendered span (or null if hidden).
 * Handles the three input forms (boolean / number / object), the dot vs.
 * number presentation, the max-overflow display ("9+"), and the colour tone.
 */
function renderBadge(badge: ButtonBadge | undefined): React.ReactNode {
  if (badge === undefined) return null;

  // Normalise the three forms into a single config object.
  const cfg =
    badge === true
      ? { dot: true }
      : typeof badge === 'number'
        ? { count: badge }
        : badge;

  const dot = cfg.dot === true || cfg.count === undefined;
  const max = cfg.max ?? 9;
  const showZero = cfg.showZero ?? false;
  const tone = cfg.tone ?? 'destructive';

  // Hide for count=0 unless explicitly opted in.
  if (!dot && cfg.count === 0 && !showZero) return null;

  if (dot) {
    return (
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute top-0.5 right-0.5',
          'size-2 rounded-full ring-2 ring-card',
          BADGE_TONE[tone],
        )}
      />
    );
  }

  const count = cfg.count!;
  const display = count > max ? `${max}+` : String(count);
  const isMultiChar = display.length > 1;

  return (
    <span
      aria-hidden
      className={cn(
        'pointer-events-none absolute -top-1 -right-1',
        'min-w-[18px] h-[18px] rounded-full ring-2 ring-card',
        'inline-flex items-center justify-center',
        'text-[10px] leading-none font-semibold tabular-nums',
        isMultiChar && 'px-1',
        BADGE_TONE[tone],
      )}
    >
      {display}
    </span>
  );
}

/** Decide whether the badge needs `position: relative` on the button. */
function badgeIsVisible(badge: ButtonBadge | undefined): boolean {
  if (badge === undefined) return false;
  if (badge === true) return true;
  if (typeof badge === 'number') return badge !== 0; // count=0 hides by default
  if (badge.dot) return true;
  if (badge.count === undefined) return false;
  if (badge.count === 0 && !badge.showZero) return false;
  return true;
}

export function Button({
  label,
  icon: Icon,
  preIcon: PreIcon,
  postIcon: PostIcon,
  loading = false,
  variant = 'default',
  size = 'default',
  orientation = 'horizontal',
  badge,
  disabled,
  disabledReason,
  className,
  ...rest
}: ButtonProps) {
  const isIconOnly = !label;
  const shadSize = isIconOnly ? ICON_SIZE[size] : SHAD_SIZE[size];

  // Loading replaces the leading icon with the shared Spinner. For icon-only
  // buttons, it replaces the icon itself. The button is also disabled.
  const spinner = loading ? <Spinner layout="inline" /> : null;

  const variantOverride = VARIANT_OVERRIDES[variant ?? 'default'];
  const sizeOverride =
    size === 'xl' ? (isIconOnly ? XL_ICON_OVERRIDE : XL_OVERRIDE) : undefined;
  const orientationOverride = orientation === 'vertical' ? VERTICAL_OVERRIDE : undefined;
  const hasBadge = badgeIsVisible(badge);

  const composedClassName = cn(
    FOCUS_RING,
    variantOverride,
    sizeOverride,
    orientationOverride,
    hasBadge && 'relative overflow-visible',
    className,
  );

  const button = (
    <ShadButton
      variant={variant}
      size={shadSize}
      disabled={!!disabled || loading}
      className={composedClassName}
      {...rest}
    >
      {isIconOnly ? (
        (spinner ?? (Icon ? <Icon /> : null))
      ) : (
        <>
          {spinner ?? (PreIcon ? <PreIcon /> : null)}
          <span className="leading-none">{label}</span>
          {PostIcon && !loading ? <PostIcon /> : null}
        </>
      )}
      {renderBadge(badge)}
    </ShadButton>
  );

  if (!disabled || disabledReason == null || disabledReason === '') return button;
  return (
    <Tooltip content={disabledReason}>
      <span className="inline-flex" tabIndex={0}>
        {button}
      </span>
    </Tooltip>
  );
}
